import { useEffect, useState } from "react"
import { useMountedRef } from "./use-mounted-ref"

// Based on https://marmelab.com/blog/2023/01/11/use-async-effect-react.html
export function useAsyncEffect<T>(
  mountCallback: (isInitialRun: boolean) => Promise<T>,
  unmountCallback: () => Promise<T>,
  deps: unknown[]
) {
  const isMounted = useMountedRef()
  const [isRunning, setIsRunning] = useState(false)
  const [error, setError] = useState<unknown>(undefined)

  useEffect(() => {
    let isIgnored = false
    let isResolved = false

    ;(async function () {
      await Promise.resolve()
      if (isIgnored) return

      setIsRunning(true)

      try {
        await mountCallback(!isMounted || isIgnored)
        isResolved = true

        if (!isIgnored) setError(undefined)
        else unmountCallback()
      } catch (error) {
        if (isMounted) setError(error)
      } finally {
        setIsRunning(false)
      }
    })()

    return () => {
      isIgnored = true
      if (isResolved) {
        unmountCallback().catch(() => {
          if (!isMounted) return
          setError(error)
        })
      }
    }
  }, deps)

  return [isRunning, error]
}
